import type { ParameterOption, ParameterType } from '@shared/types';
import { isScadColorString } from '../../colorUtils';

export type StringParseResult = {
  type: ParameterType;
  value: string | string[];
  options?: ParameterOption[];
  svgFile?: string;
  multiline?: boolean;
};

/**
 * Detects quoted string values and classifies them into:
 * - `'file'` when preceded by `// upload`
 * - `'svgcheckboxgroup'` / `'svgradiogroup'` when preceded by those directives
 * - `'color'` when name/comment contains "color" and the value is a valid SCAD color
 * - `'font'` when name/comment contains "font"
 * - `'string'` (with optional `multiline`) otherwise
 *
 * Also parses inline `[opt1, opt2, ...]` options for string parameters.
 *
 * Returns null if valueStr is not a quoted string.
 */
export function parseStringType(
  valueStr: string,
  comment: string,
  name: string,
  nextIsUpload: boolean,
  nextIsSvgCheckboxGroup: boolean,
  nextIsSvgRadioGroup: boolean,
): StringParseResult | null {
  if (!valueStr.startsWith('"') || !valueStr.endsWith('"')) return null;

  const rawString = valueStr.slice(1, -1);
  // Unescape OpenSCAD string escape sequences so the UI works with real
  // characters. \n is the key trigger for multiline mode.
  const unescaped = rawString
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');
  const multiline = rawString.includes('\\n') || undefined;

  let type: ParameterType = 'string';
  let value: string | string[] = unescaped;
  let options: ParameterOption[] | undefined = undefined;
  let svgFile: string | undefined = undefined;

  if (nextIsUpload) {
    type = 'file';
  } else if (nextIsSvgCheckboxGroup || nextIsSvgRadioGroup) {
    // value is the SVG filename; parse options from the comment [id1, id2, ...]
    const svgFilename = unescaped;
    if (comment) {
      const optionsMatch = comment.match(/\[([^\]]+)\]/);
      if (optionsMatch) {
        const rawOptions = optionsMatch[1]
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== '');
        options = rawOptions.map((opt) => {
          const cleanOpt = opt.replace(/^"|"$/g, '');
          return { label: cleanOpt, value: cleanOpt };
        });
      }
    }
    if (nextIsSvgCheckboxGroup) {
      type = 'svgcheckboxgroup';
      // All parts enabled by default — value is string[]
      value = options ? options.map((o) => String(o.value)) : [];
    } else {
      type = 'svgradiogroup';
      // First option selected by default — value is string
      value = options && options.length > 0 ? String(options[0].value) : '';
    }
    svgFile = svgFilename;
  } else if (
    (name.toLowerCase().includes('color') ||
      comment.toLowerCase().includes('color')) &&
    isScadColorString(unescaped)
  ) {
    type = 'color';
  } else if (
    name.toLowerCase().includes('font') ||
    comment.toLowerCase().includes('font')
  ) {
    type = 'font';
  }

  // Parse inline options for string types (not svggroup types)
  if (type !== 'svgcheckboxgroup' && type !== 'svgradiogroup' && comment) {
    const optionsMatch = comment.match(/\[([^\]]+)\]/);
    if (optionsMatch) {
      const rawOptions = optionsMatch[1].split(',').map((s) => s.trim());
      // Detect [value:Label, ...] labelled syntax: every token has a colon
      // with a non-empty label part (e.g. "pla:PLA").
      const isLabelledList =
        rawOptions.length > 0 &&
        rawOptions.every((tok) => {
          const colonIdx = tok.indexOf(':');
          if (colonIdx === -1) return false;
          const labelPart = tok.slice(colonIdx + 1).trim();
          return labelPart.length > 0;
        });
      if (isLabelledList) {
        options = rawOptions.map((tok) => {
          const colonIdx = tok.indexOf(':');
          const valPart = tok.slice(0, colonIdx).trim().replace(/^"|"$/g, '');
          const labelPart = tok.slice(colonIdx + 1).trim();
          return { value: valPart, label: labelPart };
        });
      } else {
        options = rawOptions.map((opt) => {
          const cleanOpt = opt.replace(/^"|"$/g, '');
          return { label: cleanOpt, value: cleanOpt };
        });
      }
    }
  }

  return { type, value, options, svgFile, multiline };
}
